import { AppContext } from '@/ui/components/Context';
import { useExtendedAccountsFilters } from '@/ui/features/contact-management/components/AccountsPanel/hooks/useExtendedAccountsFilters';
import { useOverrideMapAccountFilter } from '@/ui/features/contact-management/components/AccountsPanel/hooks/useOverrideMapAccountFilter';
import { useOverrideMapAccountFilterReverse } from '@/ui/features/contact-management/components/AccountsPanel/hooks/useOverrideMapAccountFilterReverse';
import { useExtendedUserDirectoryFilters } from '@/ui/features/contact-management/components/UserDirectoryPanel/hooks';
import { useOverrideMapUserDirectoryFilter } from '@/ui/features/contact-management/components/UserDirectoryPanel/hooks/useOverrideMapUserDirectoryFilter';
import { appName } from '@/ui/utils/utils';
import { useContactManagementCustomization } from '@juvare/alert-ui/ui/hooks/useContactManagementCustomization';
import { getErrorMessage } from '@juvare/common-web-ui/utils';
import { AccountsTableCustomization } from '@juvare/contact-management/ui/features/accounts/hooks/useAccountsTable';
import { UserDirectoryTableCustomization } from '@juvare/contact-management/ui/features/user-directory/hooks';
import { ContactManagementConfigProvider } from '@juvare/contact-management/ui/providers';
import { useCredentialingUserDirectoryColumns } from '@juvare/credentialing/ui/hooks';
import { Alert } from '@mui/material';
import React, { PropsWithChildren } from 'react';

export const ContactManagementConfigProviderPrivate: React.FC<PropsWithChildren> = ({ children }) => {
    const { notificationConfig, accountsTableCustomization } = useContactManagementCustomization();
    const { useOverrideAccountFilter } = useOverrideMapAccountFilter();
    const { useOverrideUserDirectoryFilter } = useOverrideMapUserDirectoryFilter();
    const { overrideMapAccountFilterReverse } = useOverrideMapAccountFilterReverse();
    const { extendFilters: extendAccountsFilters, environmentConfigError } = useExtendedAccountsFilters();
    const { extendFilters: extendUserDirectoryFilters } = useExtendedUserDirectoryFilters();
    const credentialingUserDirectoryColumns = useCredentialingUserDirectoryColumns();

    const getUserDirectoryDataTableProps: UserDirectoryTableCustomization['getDataTableProps'] = (defaultDataTableProps) => {
        const dataTableProps = { ...defaultDataTableProps };
        dataTableProps.columns = credentialingUserDirectoryColumns;
        return dataTableProps;
    };

    if (environmentConfigError) {
        return <Alert security='error'>{getErrorMessage(environmentConfigError)}</Alert>;
    }

    return (
        <ContactManagementConfigProvider
            appName={appName}
            appContext={AppContext}
            notificationConfig={notificationConfig}
            accountsTableCustomization={{
                getDataTableProps: accountsTableCustomization.getDataTableProps,
                extendFilters: extendAccountsFilters,
                overrideUseAccountFilter: useOverrideAccountFilter as AccountsTableCustomization['overrideUseAccountFilter'],
                overrideMapAccountFilterReverse:
                    overrideMapAccountFilterReverse as AccountsTableCustomization['overrideMapAccountFilterReverse'],
                onGetAdvancedSearchItems: (defaults) =>
                    defaults.filter((item) => !item.dynamicColumnHandlerName?.startsWith('configurableList')),
            }}
            userDirectoryTableCustomization={{
                getDataTableProps: getUserDirectoryDataTableProps,
                extendFilters: extendUserDirectoryFilters,
                overrideUseAccountFilter: useOverrideUserDirectoryFilter as UserDirectoryTableCustomization['overrideUseAccountFilter'],
            }}
        >
            {children}
        </ContactManagementConfigProvider>
    );
};
